import { getPrices } from "@breakout/sdk"
import { ZERO_ADDRESS } from "@breakout/shared/constants"
import type { Maybe } from "@breakout/shared/types/market"
import { type UseQueryOptions, useQuery } from "@tanstack/react-query"
import type { Address } from "viem"
import { useBreakoutConfig } from "../../lib/providers"
import { type MarketPrices, marketKeys } from "./useMarket"

interface MarketPricesArgs {
	marketAddress: Address
}

// staleTime: 10s (prices change with trades)
export function useMarketPrices(
	args: Maybe<MarketPricesArgs>,
	options?: Omit<UseQueryOptions<MarketPrices>, "queryKey" | "queryFn">,
) {
	const { publicClient } = useBreakoutConfig()

	return useQuery({
		queryKey: marketKeys.marketPrices(args?.marketAddress ?? ZERO_ADDRESS),
		queryFn: async (): Promise<MarketPrices> => {
			if (!args) {
				throw new Error("Market address required")
			}
			const prices = await getPrices(publicClient, { market: args.marketAddress })
			const total = prices.reduce((sum, p) => sum + p, 0)
			const normalized = prices.map(p => (total > 0 ? p / total : 0))
			return { prices, normalized }
		},
		enabled: !!args,
		staleTime: 10_000,
		...options,
	})
}
